import { getErrorMessage } from '../../utils/errorMessage';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  AlertTriangle,
  BookOpen,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  ExternalLink,
  Lightbulb,
  ListVideo,
  LoaderCircle,
  MessageSquareText,
  RefreshCw,
  Send,
  Sparkles,
  X,
} from 'lucide-react';
import { videoFirstFrameSource } from '../mediaPreview';
import HelpVisual from './HelpVisual';
import { HELP_TOPICS } from './helpContent';
import { getWorkspaceHelpContent } from './workspaceHelpContentApi';
import {
  confirmSupportTicketResolution,
  createSupportTicket,
  getRecentSupportTickets,
  supportTicketApiError,
  supportTicketRetryAfter,
} from './supportTicketApi';
import './WorkspaceHelpDrawer.css';
import { SupportTicketAttachmentInput, SupportTicketAttachmentList } from './SupportTicketAttachments';
import { useSupportTicketAttachments } from './supportTicketAttachmentState';
import { replaceBrandValue, useTeamBrand } from '../branding/TeamBrandContext';

const FALLBACK_SCENE_TOPICS = Object.freeze({
  writing: ['writing-workbench', 'agent-collaboration', 'candidate-and-facts'],
  canvas: ['canvas-basics', 'character-images', 'scene-images', 'video-audio'],
});

const SCENE_LABELS = Object.freeze({ writing: '写作台', canvas: '无限画布' });

const TABS = [
  { id: 'articles', label: '图文教程', icon: BookOpen },
  { id: 'videos', label: '视频学习', icon: ListVideo },
  { id: 'feedback', label: '提交反馈', icon: MessageSquareText },
];

function resolveInitialTab(tab) {
  return TABS.some(item => item.id === tab) ? tab : 'articles';
}

const FEEDBACK_TYPES = [
  ['usage', '使用问题'],
  ['bug', '功能异常'],
  ['generation_failure', '生成失败'],
  ['billing', '点数与付费'],
  ['feature', '功能建议'],
  ['complaint', '投诉与服务'],
  ['other', '其他'],
];

const STATUS_LABELS = Object.freeze({
  open: '待处理',
  processing: '处理中',
  resolved: '待你确认',
  closed: '已关闭',
});

const EMPTY_TICKET_ACTION = Object.freeze({
  ticketId: '',
  mode: '',
  body: '',
  idempotencyKey: '',
  loading: false,
  error: '',
  success: '',
});

const EMPTY_WORKSPACE_CONTENT = Object.freeze({
  articles: { loading: false, error: '', fallback: false, items: [] },
  videos: { loading: false, error: '', items: [] },
});

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function uuidOrNull(value) {
  const candidate = String(value || '').trim();
  return UUID_PATTERN.test(candidate) ? candidate : null;
}

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, character => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
}

function textValue(value) {
  return typeof value === 'string' ? value : '';
}

function textItems(value) {
  return Array.isArray(value) ? value.filter(item => typeof item === 'string') : [];
}

function normalizeArticleSection(section, index) {
  const source = section && typeof section === 'object' ? section : {};
  return {
    id: String(source.id || `section-${index + 1}`),
    title: textValue(source.title),
    paragraphs: textItems(source.paragraphs),
    steps: textItems(source.steps),
    bullets: textItems(source.bullets),
    prompt: textValue(source.prompt),
    tip: textValue(source.tip),
    warning: textValue(source.warning),
    visual: textValue(source.visual),
    image_url: textValue(source.image_url),
    image_alt: textValue(source.image_alt),
  };
}

function normalizeArticles(items) {
  return (Array.isArray(items) ? items : []).map((article, index) => {
    const source = article && typeof article === 'object' ? article : {};
    const contentBlocks = Array.isArray(source.content_blocks)
      ? source.content_blocks
      : Array.isArray(source.content_blocks?.sections)
        ? source.content_blocks.sections
        : Array.isArray(source.sections)
          ? source.sections
          : [];
    return {
      id: String(source.slug || source.id || `article-${index + 1}`),
      title: textValue(source.title),
      summary: textValue(source.summary),
      eyebrow: textValue(source.eyebrow),
      intro: textValue(source.intro),
      sections: contentBlocks.map(normalizeArticleSection),
    };
  });
}

function fallbackArticles(scene) {
  const accepted = new Set(FALLBACK_SCENE_TOPICS[scene] || FALLBACK_SCENE_TOPICS.writing);
  return normalizeArticles(HELP_TOPICS.filter(topic => accepted.has(topic.id)));
}

function safeVideoTarget(item) {
  for (const value of [item?.target_url, item?.video_url]) {
    const candidate = textValue(value).trim();
    if (candidate.startsWith('/') || /^https?:\/\//i.test(candidate)) return candidate;
  }
  return '';
}

function normalizeVideos(items) {
  return (Array.isArray(items) ? items : []).map((item, index) => {
    const source = item && typeof item === 'object' ? item : {};
    return {
      id: String(source.id || `video-${index + 1}`),
      title: textValue(source.title) || '未命名视频教程',
      summary: textValue(source.summary),
      cover_url: textValue(source.cover_url),
      video_url: textValue(source.video_url),
      target_url: safeVideoTarget(source),
      creator: textValue(source.creator),
    };
  }).filter(item => item.target_url).slice(0, 6);
}

function ticketNumber(ticket) {
  return ticket?.ticket_no || ticket?.ticket_number || ticket?.number || ticket?.id || '';
}

function legacyTicketReply(ticket) {
  const direct = ticket?.latest_reply || ticket?.final_reply || ticket?.latest_admin_reply || ticket?.admin_reply || ticket?.resolution || ticket?.reply || ticket?.reply_content;
  if (typeof direct === 'string') return { body: direct, created_at: ticket?.updated_at || null };
  if (direct?.body) return direct;
  if (ticket?.latest_message?.sender_type === 'admin') return ticket.latest_message;
  if (ticket?.last_message?.sender_type === 'admin') return ticket.last_message;
  return null;
}

function ticketMessages(ticket) {
  const messages = Array.isArray(ticket?.messages)
    ? ticket.messages.filter(message => typeof message?.body === 'string' && message.body.trim())
    : [];
  if (messages.length) return messages;

  const reply = legacyTicketReply(ticket);
  return reply ? [{ ...reply, sender_type: 'admin', _legacy: true }] : [];
}

function ticketMessageSender(message) {
  if (message?.sender_type === 'admin') return '运营';
  if (message?.sender_type === 'user') return '你';
  return '系统';
}

function ticketMessageTime(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return new Intl.DateTimeFormat('zh-CN', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
}

function ticketList(data) {
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function ArticleSection({ section }) {
  return (
    <section className="workspace-help-article-section">
      <h4>{section.title}</h4>
      {section.paragraphs?.map((paragraph, index) => <p key={`${section.id}-paragraph-${index}`}>{paragraph}</p>)}
      {section.steps.length > 0 && (
        <ol>
          {section.steps.map((step, index) => <li key={`${section.id}-step-${index}`}><span>{index + 1}</span><p>{step}</p></li>)}
        </ol>
      )}
      {section.bullets.length > 0 && (
        <ul>
          {section.bullets.map((bullet, index) => <li key={`${section.id}-bullet-${index}`}><Check size={13} /><span>{bullet}</span></li>)}
        </ul>
      )}
      {section.prompt && <blockquote><Sparkles size={14} /><span>“{section.prompt}”</span></blockquote>}
      {section.tip && <aside className="is-tip"><Lightbulb size={14} /><p>{section.tip}</p></aside>}
      {section.warning && <aside className="is-warning"><AlertTriangle size={14} /><p>{section.warning}</p></aside>}
      {section.image_url && <figure className="workspace-help-article-image"><img src={section.image_url} alt={section.image_alt || `${section.title || '教程'}示意图`} loading="lazy" /></figure>}
      {section.visual && <HelpVisual type={section.visual} />}
    </section>
  );
}

function ArticlePanel({ scene, state, onRetry }) {
  const brand = useTeamBrand();
  const topics = useMemo(
    () => replaceBrandValue(state.items, brand.active ? brand.name : ''),
    [brand.active, brand.name, state.items],
  );
  const [activeId, setActiveId] = useState(topics[0]?.id || '');

  useEffect(() => { setActiveId(topics[0]?.id || ''); }, [topics]);
  if (state.loading) {
    return <div className="workspace-help-state" role="status"><LoaderCircle className="is-spinning" size={19} /><span>正在加载图文教程…</span></div>;
  }
  if (state.error && !state.fallback) {
    return <div className="workspace-help-state is-error" role="alert"><p>{state.error}</p><button type="button" onClick={onRetry}><RefreshCw size={13} />重新加载</button></div>;
  }
  const active = topics.find(topic => topic.id === activeId) || topics[0];
  if (!active) return <div className="workspace-help-empty">当前场景暂时没有图文教程。</div>;

  return (
    <div className="workspace-help-articles">
      {state.fallback && <div className="workspace-help-fallback" role="alert"><span>在线教程暂时无法加载，当前显示内置教程。</span><button type="button" onClick={onRetry}><RefreshCw size={12} />重试</button></div>}
      <nav aria-label={`${SCENE_LABELS[scene]}帮助主题`}>
        {topics.map(topic => (
          <button
            type="button"
            key={topic.id}
            className={topic.id === active.id ? 'is-active' : ''}
            onClick={() => setActiveId(topic.id)}
            aria-current={topic.id === active.id ? 'page' : undefined}
          >
            <span><strong>{topic.title}</strong><small>{topic.summary}</small></span>
            <ChevronRight size={14} />
          </button>
        ))}
      </nav>
      <article>
        <header><span>{active.eyebrow}</span><h3>{active.title}</h3><p>{active.intro}</p></header>
        {active.sections.map(section => <ArticleSection key={section.id} section={section} />)}
      </article>
    </div>
  );
}

function VideoPanel({ state, onRetry }) {
  const brand = useTeamBrand();
  if (state.loading) {
    return <div className="workspace-help-state" role="status"><LoaderCircle className="is-spinning" size={19} /><span>正在加载视频教程…</span></div>;
  }
  if (state.error) {
    return <div className="workspace-help-state is-error" role="alert"><p>{state.error}</p><button type="button" onClick={onRetry}><RefreshCw size={13} />重新加载</button></div>;
  }
  if (!state.items.length) {
    return <div className="workspace-help-state"><ListVideo size={22} /><strong>暂时没有可播放的视频</strong><span>可以先查看图文教程，视频内容发布后会自动出现在这里。</span></div>;
  }
  return (
    <div className="workspace-help-videos">
      <header><div><h3>边看边学</h3><p>视频会在新标签页打开，当前创作现场不会被打断。</p></div><span>{state.items.length} 个</span></header>
      <div>
        {state.items.map(item => {
          const previewImage = item.cover_url;
          return (
            <a key={item.id} href={item.target_url} target="_blank" rel="noopener noreferrer">
              <span className="workspace-help-video-cover">
                {previewImage
                  ? <img src={previewImage} alt="" />
                  : item.video_url
                    ? <video src={videoFirstFrameSource(item.video_url)} muted playsInline preload="metadata" aria-hidden="true" />
                    : <span className="workspace-help-video-placeholder"><ListVideo size={22} /></span>}
                <i><ExternalLink size={14} /></i>
              </span>
              <span className="workspace-help-video-copy"><strong>{brand.text(item.title)}</strong><small>{brand.text(item.summary || '视频教程')} · {brand.text(item.creator || '小影实验室')}</small></span>
            </a>
          );
        })}
      </div>
    </div>
  );
}

function FeedbackPanel({
  scene,
  contextLabel,
  relatedId,
  preview,
  tickets,
  loadTickets,
  form,
  setForm,
  submitState,
  onSubmit,
  ticketAction,
  beginUnresolved,
  updateUnresolved,
  confirmResolution,
  feedbackUploads,
  unresolvedUploads,
}) {
  const formValid = form.title.trim().length >= 2 && form.body.trim().length >= 10;
  const retryAfter = Math.max(0, Number(submitState.retryAfter) || 0);
  const update = (field, value) => setForm(current => ({ ...current, [field]: value }));
  return (
    <div className="workspace-help-feedback-panel">
      <section className="workspace-help-feedback-form">
        <header><h3>把问题交给运营处理</h3><p>自动附带页面和项目标识；附件只上传你主动选择的文件。</p></header>
        {preview && <div className="workspace-help-preview-note"><CircleHelp size={14} /><span>当前是界面预览，反馈提交已停用。</span></div>}
        <form onSubmit={onSubmit}>
          <label><span>问题类型</span><select value={form.type} onChange={event => update('type', event.target.value)} disabled={preview || submitState.loading}>{FEEDBACK_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label><span>标题</span><input value={form.title} onChange={event => update('title', event.target.value.slice(0, 120))} maxLength={120} disabled={preview || submitState.loading} placeholder="例如：生成按钮一直停在处理中" /><small>{form.title.length}/120</small></label>
          <label><span>详细说明</span><textarea value={form.body} onChange={event => update('body', event.target.value.slice(0, 2000))} maxLength={2000} disabled={preview || submitState.loading} placeholder="请写清做了什么、看到了什么，以及你原本希望发生什么。" /><small>{form.body.length}/2000</small></label>
          <SupportTicketAttachmentInput uploads={feedbackUploads} disabled={preview || submitState.loading} />
          <div className="workspace-help-context"><span>自动附带</span><code>{contextLabel || SCENE_LABELS[scene] || SCENE_LABELS.writing} · {relatedId || '当前页面'}</code></div>
          {submitState.success && <div className="workspace-help-submit-result is-success" role="status"><Check size={14} /><span>反馈已提交，工单号：<strong>{submitState.success}</strong>。处理结果会通过站内消息提醒你。</span></div>}
          {submitState.error && <div className="workspace-help-submit-result is-error" role="alert"><AlertTriangle size={14} /><span>{submitState.error}</span></div>}
          <button type="submit" className="workspace-help-submit" disabled={preview || !formValid || !feedbackUploads.ready || submitState.loading || retryAfter > 0} title={!preview && !formValid ? '标题至少 2 个字，详细说明至少 10 个字' : undefined}>
            {submitState.loading ? <LoaderCircle className="is-spinning" size={14} /> : <Send size={14} />}
            {submitState.loading ? '正在提交…' : retryAfter > 0 ? `${retryAfter} 秒后可重试` : submitState.error ? '重新提交' : '提交反馈'}
          </button>
        </form>
      </section>

      <section className="workspace-help-recent">
        <header><div><h3>最近反馈</h3><p>最多展示当前场景最近 5 条。</p></div>{!preview && <button type="button" onClick={loadTickets} disabled={tickets.loading} aria-label="刷新最近反馈"><RefreshCw className={tickets.loading ? 'is-spinning' : ''} size={14} /></button>}</header>
        {preview ? <div className="workspace-help-empty">正式环境登录后可查看反馈进度。</div>
          : tickets.loading ? <div className="workspace-help-recent-state" role="status"><LoaderCircle className="is-spinning" size={15} />正在读取处理进度…</div>
            : tickets.error ? <div className="workspace-help-recent-state is-error" role="alert"><span>{tickets.error}</span><button type="button" onClick={loadTickets}>重试</button></div>
              : tickets.items.length ? <div className="workspace-help-ticket-list">{tickets.items.map(ticket => {
                const messages = ticketMessages(ticket);
                const status = ticket.status || 'open';
                const actionActive = ticketAction.ticketId === String(ticket.id || '');
                return <article key={ticket.id || ticketNumber(ticket)}>
                  <header><strong>{ticket.title || '未命名反馈'}</strong><span className={`is-${status}`}>{STATUS_LABELS[status] || status}</span></header>
                  <small><Clock3 size={11} />{ticketNumber(ticket) ? `工单 ${ticketNumber(ticket)}` : '反馈记录'}</small>
                  {messages.length ? <ol className="workspace-help-ticket-timeline" aria-label={`${ticketNumber(ticket) ? `工单 ${ticketNumber(ticket)}` : '当前工单'}沟通记录`}>
                    {messages.map((message, index) => {
                      const senderType = ['admin', 'user'].includes(message?.sender_type) ? message.sender_type : 'system';
                      const messageTime = ticketMessageTime(message?.created_at);
                      return <li className={`is-${senderType}`} key={message?.id || `${senderType}-${message?.created_at || 'unknown'}-${index}`}>
                        <div><strong>{ticketMessageSender(message)}</strong>{messageTime && <time dateTime={String(message.created_at)} aria-label={`发送时间 ${messageTime}`}>{messageTime}</time>}</div>
                        <p>{message.body}</p>
                        <SupportTicketAttachmentList attachments={message.attachments} messageId={message.id} />
                      </li>;
                    })}
                  </ol> : <div className="workspace-help-ticket-conversation-empty">暂无沟通记录，运营回复后会显示在这里。</div>}
                  {actionActive && ticketAction.success && <div className="workspace-help-ticket-action-result is-success" role="status"><Check size={13} />{ticketAction.success}</div>}
                  {status === 'resolved' && !ticketAction.success && <div className="workspace-help-ticket-confirmation">
                    <div><strong>这个问题已经解决了吗？</strong><span>确认后工单才会关闭；仍有问题可以补充说明并继续处理。</span></div>
                    {actionActive && ticketAction.error && <div className="workspace-help-ticket-action-result is-error" role="alert"><AlertTriangle size={13} />{ticketAction.error}</div>}
                    {actionActive && ticketAction.mode === 'unresolved' && <label>
                      <span>补充未解决情况</span>
                      <textarea aria-label="补充未解决情况" value={ticketAction.body} maxLength={5000} disabled={ticketAction.loading} onChange={event => updateUnresolved(event.target.value)} placeholder="请说明还有哪里不对，方便运营继续处理。" />
                      <small>{ticketAction.body.length}/5000</small>
                    </label>}
                    {actionActive && ticketAction.mode === 'unresolved' && <SupportTicketAttachmentInput uploads={unresolvedUploads} disabled={ticketAction.loading} label="添加补充附件" />}
                    <div className="workspace-help-ticket-actions">
                      <button type="button" disabled={ticketAction.loading} onClick={() => confirmResolution(ticket, true)}>{actionActive && ticketAction.loading && ticketAction.mode === 'confirmed' ? <LoaderCircle className="is-spinning" size={13} /> : <Check size={13} />}确认已解决</button>
                      {actionActive && ticketAction.mode === 'unresolved'
                        ? <button type="button" className="is-primary" disabled={ticketAction.loading || !unresolvedUploads.ready || ticketAction.body.trim().length < 2} onClick={() => confirmResolution(ticket, false)}>{ticketAction.loading ? <LoaderCircle className="is-spinning" size={13} /> : <Send size={13} />}提交并继续处理</button>
                        : <button type="button" disabled={ticketAction.loading} onClick={() => beginUnresolved(ticket)}>仍有问题</button>}
                    </div>
                  </div>}
                </article>;
              })}</div>
                : <div className="workspace-help-empty">这个场景还没有提交过反馈。</div>}
      </section>
    </div>
  );
}

export default function WorkspaceHelpDrawer({
  open,
  onClose,
  scene = 'writing',
  relatedId = null,
  preview = false,
  initialTab = 'articles',
  sourceLabel = '',
}) {
  const brand = useTeamBrand();
  const [activeTab, setActiveTab] = useState(() => resolveInitialTab(initialTab));
  const [workspaceContent, setWorkspaceContent] = useState(EMPTY_WORKSPACE_CONTENT);
  const [tickets, setTickets] = useState({ loading: false, error: '', items: [] });
  const [form, setFormState] = useState({ type: 'usage', title: '', body: '' });
  const [submitState, setSubmitState] = useState({ loading: false, error: '', success: '', retryAfter: 0 });
  const [ticketAction, setTicketAction] = useState(EMPTY_TICKET_ACTION);
  const closeRef = useRef(null);
  const onCloseRef = useRef(onClose);
  const retryPayloadRef = useRef(null);
  const submitInFlightRef = useRef(false);
  const workspaceContentRequestRef = useRef(0);
  const feedbackUploads = useSupportTicketAttachments(() => {
    retryPayloadRef.current = null;
    setSubmitState(current => ({ ...current, error: '', success: '' }));
  });
  const unresolvedUploads = useSupportTicketAttachments(() => {
    setTicketAction(current => ({ ...current, idempotencyKey: createClientRequestId(), error: '', success: '' }));
  });

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!(submitState.retryAfter > 0)) return undefined;
    const timer = window.setTimeout(() => {
      setSubmitState(current => {
        const retryAfter = Math.max(0, (Number(current.retryAfter) || 0) - 1);
        return { ...current, retryAfter, error: retryAfter > 0 ? current.error : '' };
      });
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [submitState.retryAfter]);

  const setForm = useCallback(update => {
    retryPayloadRef.current = null;
    setSubmitState(current => ({ ...current, error: '', success: '' }));
    setFormState(update);
  }, []);

  const loadWorkspaceContent = useCallback(async () => {
    const requestId = workspaceContentRequestRef.current + 1;
    workspaceContentRequestRef.current = requestId;
    setWorkspaceContent({
      articles: { loading: true, error: '', fallback: false, items: [] },
      videos: { loading: true, error: '', items: [] },
    });
    try {
      const data = await getWorkspaceHelpContent(scene);
      if (workspaceContentRequestRef.current !== requestId) return;
      setWorkspaceContent({
        articles: { loading: false, error: '', fallback: false, items: normalizeArticles(data.articles) },
        videos: { loading: false, error: '', items: normalizeVideos(data.videos) },
      });
    } catch (error) {
      if (workspaceContentRequestRef.current !== requestId) return;
      const message = error?.message || '帮助内容加载失败';
      setWorkspaceContent({
        articles: { loading: false, error: getErrorMessage(message), fallback: true, items: fallbackArticles(scene) },
        videos: { loading: false, error: getErrorMessage(message), items: [] },
      });
    }
  }, [scene]);

  const loadTickets = useCallback(async () => {
    if (preview) return;
    setTickets(current => ({ ...current, loading: true, error: '' }));
    try {
      const data = await getRecentSupportTickets(scene);
      setTickets({ loading: false, error: '', items: ticketList(data).slice(0, 5) });
    } catch (error) {
      setTickets({ loading: false, error: getErrorMessage(supportTicketApiError(error, '反馈记录加载失败，请重试')), items: [] });
    }
  }, [preview, scene]);

  const beginUnresolved = ticket => {
    unresolvedUploads.clear();
    setTicketAction({
      ...EMPTY_TICKET_ACTION,
      ticketId: String(ticket?.id || ''),
      mode: 'unresolved',
      idempotencyKey: createClientRequestId(),
    });
  };

  const updateUnresolved = useCallback(body => {
    setTicketAction(current => ({ ...current, body: body.slice(0, 5000), idempotencyKey: createClientRequestId(), error: '', success: '' }));
  }, []);

  const confirmResolution = async (ticket, confirmed) => {
    const ticketId = String(ticket?.id || '');
    if (!ticketId || ticketAction.loading || (!confirmed && !unresolvedUploads.ready)) return;
    const current = ticketAction.ticketId === ticketId ? ticketAction : EMPTY_TICKET_ACTION;
    const body = current.body.trim();
    if (!confirmed && body.length < 2) {
      setTicketAction({ ...current, ticketId, mode: 'unresolved', error: '请至少填写 2 个字，说明问题仍未解决的地方' });
      return;
    }
    const idempotencyKey = current.idempotencyKey || createClientRequestId();
    setTicketAction({ ...current, ticketId, mode: confirmed ? 'confirmed' : 'unresolved', idempotencyKey, loading: true, error: '', success: '' });
    try {
      await confirmSupportTicketResolution({
        id: ticketId,
        confirmed,
        idempotency_key: idempotencyKey,
        ...(!confirmed ? { body } : {}),
        ...(!confirmed && unresolvedUploads.items.length ? { attachments: unresolvedUploads.tokens() } : {}),
      });
      unresolvedUploads.clear();
      setTicketAction({
        ...EMPTY_TICKET_ACTION,
        ticketId,
        success: confirmed ? '已确认解决，工单已经关闭。' : '补充说明已提交，工单已恢复处理中。',
      });
      await loadTickets();
    } catch (error) {
      setTicketAction({ ...current, ticketId, mode: confirmed ? 'confirmed' : 'unresolved', idempotencyKey, loading: false, error: getErrorMessage(supportTicketApiError(error, '反馈确认失败，请重试')), success: '' });
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    loadWorkspaceContent();
    return () => { workspaceContentRequestRef.current += 1; };
  }, [loadWorkspaceContent, open, scene]);

  useEffect(() => {
    if (!open || activeTab !== 'feedback' || preview) return;
    loadTickets();
  }, [activeTab, loadTickets, open, preview]);

  useEffect(() => {
    if (!open) return undefined;
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = event => {
      if (event.key === 'Escape') { onCloseRef.current?.(); return; }
      if (event.key !== 'Tab') return;
      const drawer = closeRef.current?.closest('.workspace-help-drawer');
      const focusable = [...(drawer?.querySelectorAll('button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])') || [])];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    window.requestAnimationFrame(() => closeRef.current?.focus());
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setActiveTab(resolveInitialTab(initialTab));
    setSubmitState(current => ({
      loading: false,
      error: current.retryAfter > 0 ? current.error : '',
      success: '',
      retryAfter: Math.max(0, Number(current.retryAfter) || 0),
    }));
    setTicketAction(EMPTY_TICKET_ACTION);
    retryPayloadRef.current = null;
  }, [initialTab, open, scene]);

  const submit = async event => {
    event.preventDefault();
    if (preview || submitState.loading || submitState.retryAfter > 0 || submitInFlightRef.current || !feedbackUploads.ready) return;
    const title = form.title.trim();
    const body = form.body.trim();
    if (title.length < 2 || body.length < 10) return;
    const payload = retryPayloadRef.current || {
      client_request_id: createClientRequestId(),
      scene,
      type: form.type,
      title,
      body,
      page_path: window.location.pathname,
      related_project_id: uuidOrNull(relatedId),
      ...(feedbackUploads.items.length ? { attachments: feedbackUploads.tokens() } : {}),
    };
    retryPayloadRef.current = payload;
    submitInFlightRef.current = true;
    setSubmitState({ loading: true, error: '', success: '', retryAfter: 0 });
    try {
      const created = await createSupportTicket(payload);
      const number = ticketNumber(created?.ticket || created) || '已创建';
      retryPayloadRef.current = null;
      setSubmitState({ loading: false, error: '', success: String(number), retryAfter: 0 });
      setFormState({ type: 'usage', title: '', body: '' });
      feedbackUploads.clear();
      await loadTickets();
    } catch (error) {
      setSubmitState({
        loading: false,
        error: getErrorMessage(supportTicketApiError(error)),
        success: '',
        retryAfter: supportTicketRetryAfter(error),
      });
    } finally {
      submitInFlightRef.current = false;
    }
  };

  if (!open) return null;
  const sceneLabel = SCENE_LABELS[scene] || SCENE_LABELS.writing;
  const drawerLabel = sourceLabel ? `${sourceLabel}反馈` : `${sceneLabel}帮助`;
  return createPortal(
    <div className="workspace-help-overlay" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) onCloseRef.current?.(); }}>
      <section id="workspace-help-drawer" className="workspace-help-drawer" role="dialog" aria-modal="true" aria-labelledby="workspace-help-title">
        <header className="workspace-help-header">
          <div><span><CircleHelp size={15} />{drawerLabel}</span><h2 id="workspace-help-title">{sourceLabel ? brand.text('把遇到的问题交给小影团队') : '边做边查，不用离开当前工作'}</h2></div>
          <button ref={closeRef} type="button" onClick={onClose} aria-label="关闭帮助"><X size={18} /></button>
        </header>
        <div className="workspace-help-tabs" role="tablist" aria-label="帮助内容">
          {TABS.map(({ id, label, icon: Icon }) => <button type="button" role="tab" id={`workspace-help-tab-${id}`} aria-controls={`workspace-help-panel-${id}`} aria-selected={activeTab === id} className={activeTab === id ? 'is-active' : ''} onClick={() => setActiveTab(id)} key={id}><Icon size={14} />{label}</button>)}
        </div>
        <div className="workspace-help-body" role="tabpanel" id={`workspace-help-panel-${activeTab}`} aria-labelledby={`workspace-help-tab-${activeTab}`}>
          {activeTab === 'articles' && <ArticlePanel scene={scene} state={workspaceContent.articles} onRetry={loadWorkspaceContent} />}
          {activeTab === 'videos' && <VideoPanel state={workspaceContent.videos} onRetry={loadWorkspaceContent} />}
          {activeTab === 'feedback' && <FeedbackPanel scene={scene} contextLabel={sourceLabel} relatedId={relatedId} preview={preview} tickets={tickets} loadTickets={loadTickets} form={form} setForm={setForm} submitState={submitState} onSubmit={submit} ticketAction={ticketAction} beginUnresolved={beginUnresolved} updateUnresolved={updateUnresolved} confirmResolution={confirmResolution} feedbackUploads={feedbackUploads} unresolvedUploads={unresolvedUploads} />}
        </div>
      </section>
    </div>,
    document.body,
  );
}
